import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { Briefcase, Clock, Palmtree, Plus, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useEvents } from '../../contexts/EventsContext';
import { useAuth } from '../../contexts/AuthContext';
import DatePicker from '../ui/DatePicker';
import TimePicker from '../ui/TimePicker'; // Assicurati che il path sia corretto
import { useTranslation } from 'react-i18next';
import { DEFAULT_OPEN_TIME, DEFAULT_CLOSE_TIME } from '@/lib/constants';

interface StaffTimeOffTabProps {
    userId: string;
    busySlots: any[];
    readOnly?: boolean;
}

const StaffTimeOffTab: React.FC<StaffTimeOffTabProps> = ({ userId, busySlots, readOnly = false }) => {
    const { t } = useTranslation();
    const { createBusySlot, deleteBusySlot } = useEvents();
    const { organization } = useAuth();

    const [timeOffTab, setTimeOffTab] = useState<'vacation' | 'permission'>('vacation');
    const [isAdding, setIsAdding] = useState(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const [vacationStart, setVacationStart] = useState<Date | undefined>();
    const [vacationEnd, setVacationEnd] = useState<Date | undefined>();
    const [permDate, setPermDate] = useState<Date | undefined>();
    const [permStart, setPermStart] = useState('');
    const [permEnd, setPermEnd] = useState('');

    const userSlots = busySlots.filter(s => s.hairdresser_id === userId);
    const vacations = userSlots.filter(s => s.type === 'holiday');
    const permissions = userSlots.filter(s => s.type === 'permit');

    const handleAdd = async () => {
        if (!organization?.id) return toast.error(t('common.org_missing'));
        setIsAdding(true);
        try {
            let payload: any = { shop_id: organization.id, hairdresser_id: userId };

            if (timeOffTab === 'vacation') {
                if (!vacationStart || !vacationEnd) { setIsAdding(false); return toast.error(t('staff.timeoff.toast.dates_missing')); }
                if (vacationEnd < vacationStart) { setIsAdding(false); return toast.error(t('staff.timeoff.toast.date_end_error')); }
                payload = { ...payload, type: 'holiday', start_date: format(vacationStart, 'yyyy-MM-dd'), end_date: format(vacationEnd, 'yyyy-MM-dd') };
            } else {
                if (!permDate || !permStart || !permEnd) { setIsAdding(false); return toast.error(t('staff.timeoff.toast.data_missing')); }
                const d = format(permDate, 'yyyy-MM-dd');
                payload = { ...payload, type: 'permit', start_date: d, end_date: d, start_time: permStart, end_time: permEnd };
            }

            const res = await createBusySlot(payload);
            if (res.success) {
                toast.success(t('staff.timeoff.toast.added'));
                setVacationStart(undefined); setVacationEnd(undefined);
                setPermDate(undefined); setPermStart(''); setPermEnd('');
            } else {
                toast.error(t('common.error'));
            }
        } catch (e) { toast.error(t('common.connection_error')); } finally { setIsAdding(false); }
    };

    const handleDelete = async (id: string) => {
        setDeletingId(id);
        try {
            const res = await deleteBusySlot(id);
            if (res.success) toast.success(t('staff.timeoff.toast.deleted')); else toast.error(t('common.error'));
        } catch (e) { toast.error(t('common.error')); } finally { setDeletingId(null); }
    };

    const getShopHours = (date: Date) => {
        if (!organization?.time_ranges) return { open: DEFAULT_OPEN_TIME, close: DEFAULT_CLOSE_TIME };
        const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const dayName = days[date.getDay()];
        const schedule = organization.time_ranges[dayName];

        if (!schedule || !schedule.open) return { open: DEFAULT_OPEN_TIME, close: DEFAULT_CLOSE_TIME };

        // Fix: Use open/close directly from schedule
        return {
            open: schedule.open || DEFAULT_OPEN_TIME,
            close: schedule.close || DEFAULT_CLOSE_TIME
        };
    };

    const tabContentVariants = {
        hidden: { opacity: 0, y: 10 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
        exit: { opacity: 0, y: -10, transition: { duration: 0.2 } }
    };

    return (
        <motion.div
            key="timeoff"
            variants={tabContentVariants}
            initial="hidden" animate="visible" exit="exit"
            className="space-y-6 pt-4 pb-10 px-6"
        >
            <div className="bg-gray-100 p-1 rounded-xl inline-flex w-full md:w-auto">
                <button onClick={() => setTimeOffTab('vacation')} className={`flex-1 md:flex-none px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${timeOffTab === 'vacation' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>{t('staff.timeoff.vacation_tab')}</button>
                <button onClick={() => setTimeOffTab('permission')} className={`flex-1 md:flex-none px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${timeOffTab === 'permission' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>{t('staff.timeoff.permission_tab')}</button>
            </div>

            {!readOnly && (
                <div className="bg-gray-50 rounded-2xl p-4 md:p-6 border border-gray-100">
                    {timeOffTab === 'vacation' ? (
                        <div className="flex flex-col md:flex-row items-end gap-4">
                            <div className="w-full md:flex-1 space-y-1.5"><label className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('staff.timeoff.from')}</label><DatePicker selected={vacationStart} onSelect={setVacationStart} placeholder={t('staff.timeoff.start_placeholder')} minDate={new Date()} allowClosedDays={true} /></div>
                            <div className="w-full md:flex-1 space-y-1.5"><label className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('staff.timeoff.to')}</label><DatePicker selected={vacationEnd} onSelect={setVacationEnd} placeholder={t('staff.timeoff.end_placeholder')} minDate={vacationStart || new Date()} allowClosedDays={true} /></div>
                            <button onClick={handleAdd} disabled={isAdding} className="w-full md:w-auto h-[42px] px-6 bg-gray-900 text-white font-bold rounded-xl hover:bg-black transition-colors flex items-center justify-center gap-2">{isAdding ? <Loader2 className="animate-spin w-4 h-4" /> : <Plus size={16} />} {t('common.add')}</button>
                        </div>
                    ) : (
                        <div className="flex flex-col md:flex-row items-end gap-4">
                            <div className="w-full md:flex-1 space-y-1.5">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('staff.timeoff.day')}</label>
                                <DatePicker selected={permDate} onSelect={setPermDate} placeholder={t('staff.timeoff.day')} minDate={new Date()} />
                            </div>
                            <div className="flex w-full md:w-auto gap-4">
                                <div className="flex-1 md:w-32 space-y-1.5">
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('staff.timeoff.start_time')}</label>
                                    <TimePicker
                                        value={permStart}
                                        onChange={setPermStart}
                                        disabled={!permDate}
                                        minTime={permDate ? getShopHours(permDate).open : DEFAULT_OPEN_TIME}
                                        maxTime={permDate ? getShopHours(permDate).close : DEFAULT_CLOSE_TIME}
                                    />
                                </div>
                                <div className="flex-1 md:w-32 space-y-1.5">
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('staff.timeoff.end_time')}</label>
                                    <TimePicker
                                        value={permEnd}
                                        onChange={setPermEnd}
                                        disabled={!permDate}
                                        minTime={permDate ? getShopHours(permDate).open : DEFAULT_OPEN_TIME}
                                        maxTime={permDate ? getShopHours(permDate).close : DEFAULT_CLOSE_TIME}
                                        disableBefore={permStart}
                                    />
                                </div>
                            </div>
                            <button onClick={handleAdd} disabled={isAdding} className="w-full md:w-auto h-[42px] px-6 bg-gray-900 text-white font-bold rounded-xl hover:bg-black transition-colors flex items-center justify-center gap-2">{isAdding ? <Loader2 className="animate-spin w-4 h-4" /> : <Plus size={16} />} {t('common.add')}</button>
                        </div>
                    )}
                </div>
            )}

            <div className="space-y-2">
                <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                    {timeOffTab === 'vacation' ? <Briefcase size={16} className="text-clip-500" /> : <Clock size={16} className="text-clip-500" />}
                    {timeOffTab === 'vacation' ? t('staff.timeoff.vacation_planned') : t('staff.timeoff.permission_requested')}
                </h4>
                {(timeOffTab === 'vacation' ? vacations : permissions).map((v) => (
                    <div key={v.id} className="flex justify-between items-center p-3 bg-white border border-gray-100 rounded-xl hover:shadow-sm transition-shadow">
                        <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${timeOffTab === 'vacation' ? 'bg-orange-50 text-orange-600' : 'bg-blue-50 text-blue-600'}`}>
                                {timeOffTab === 'vacation' ? <Palmtree size={18} /> : <Clock size={18} />}
                            </div>
                            <div className="text-sm font-medium text-gray-700">
                                {timeOffTab === 'vacation' ? (
                                    <><span className="font-bold text-gray-900">{format(new Date(v.start_date), 'dd/MM/yyyy')}</span> <span className="text-gray-400 px-1">➜</span> <span className="font-bold text-gray-900">{format(new Date(v.end_date), 'dd/MM/yyyy')}</span></>
                                ) : (
                                    <><div className="text-xs font-bold text-gray-500 uppercase">{format(new Date(v.start_date), 'dd MMM yyyy')}</div><span className="font-bold text-gray-900">{v.start_time?.slice(0, 5)} - {v.end_time?.slice(0, 5)}</span></>
                                )}
                            </div>
                        </div>
                        {!readOnly && (
                            <button onClick={() => handleDelete(v.id)} disabled={deletingId === v.id} className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">{deletingId === v.id ? <Loader2 className="animate-spin" size={16} /> : <Trash2 size={16} />}</button>
                        )}
                    </div>
                ))}
                {(timeOffTab === 'vacation' ? vacations : permissions).length === 0 && <p className="text-sm text-gray-400 text-center py-4 bg-gray-50 rounded-xl border border-dashed">{t('common.no_data')}</p>}
            </div>
        </motion.div>
    );
};

export default StaffTimeOffTab;